import React from 'react';

import { Chip, IconButton, Link, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import EmailIcon from '@material-ui/icons/Email';
import PersonIcon from '@material-ui/icons/Person';
import PhoneIcon from '@material-ui/icons/Phone';
import i18n from 'i18n-js';

import { ReservationDTO, ReservationStatus } from 'api/controllers/reservations';
import { theme } from 'config/theme';
import {
    formatAssignedTables,
    formatDuration,
    formatGuestName,
    formatGuestsCount,
    formatReservationDate,
    formatSeats,
    getSelectedSeats,
    getTimePart,
} from 'helpers/reservationHelper';
import { ReservableTable, useAllergens } from '../hooks';

const useStyles = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        width: 460,
        borderLeft: `1px solid ${theme.palette.secondary.light}`,
        backgroundColor: 'rgba(231, 231, 231, 0.3)',
    },
    content: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        padding: 20,
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: 20,
        fontWeight: 500,
        marginBottom: 12,
    },
    statusRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    statusChip: {
        color: theme.palette.common.white,
    },
    caption: {
        fontSize: 10,
        letterSpacing: 1.25,
        textTransform: 'uppercase',
        color: theme.palette.common.black,
        margin: '20px 0 8px',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        rowGap: 12,
    },
    label: {
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    value: {
        fontSize: 16,
    },
    muted: {
        color: 'rgba(0, 0, 0, 0.38)',
    },
    contact: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        fontSize: 14,
        marginBottom: 8,
    },
    chips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    text: {
        fontSize: 14,
        whiteSpace: 'pre-wrap',
    },
    footer: {
        display: 'flex',
        gap: 16,
        flexShrink: 0,
        padding: 20,
        backgroundColor: '#f8f8f8',
        borderTop: `1px solid ${theme.palette.grey[300]}`,
        '& > *': {
            flex: 1,
        },
    },
});

export function ReservationStatusChip({ status }: { status: ReservationStatus }) {
    const classes = useStyles();
    return (
        <Chip
            size="small"
            className={classes.statusChip}
            style={{
                backgroundColor:
                    status === 'Requested'
                        ? theme.palette.warning.main
                        : theme.palette.primary.main,
            }}
            label={i18n.t(`reservations.status.${status.toLowerCase()}`)}
        />
    );
}

interface ReservationDetailsPanelProps {
    reservation: ReservationDTO;
    tables: ReservableTable[];
    onClose: () => void;
    footer?: React.ReactNode;
}

export default function ReservationDetailsPanel({
    reservation,
    tables,
    onClose,
    footer,
}: ReservationDetailsPanelProps) {
    const classes = useStyles();
    const allergens = useAllergens();
    const assignedTables = formatAssignedTables(reservation.tables, tables);
    const allergies = allergens
        .filter((allergen) => reservation.allergies.includes(allergen.id))
        .map((allergen) => allergen.name)
        .concat(reservation.otherAllergies ? [reservation.otherAllergies] : []);
    const channel = i18n.t(`reservations.channels.${reservation.channel}`);

    const field = (label: string, value: React.ReactNode) => (
        <div>
            <div className={classes.label}>{label}</div>
            <div className={classes.value}>{value}</div>
        </div>
    );

    return (
        <div className={classes.root}>
            <div className={classes.content}>
                <div className={classes.titleRow}>
                    {formatGuestName(reservation)}
                    <IconButton size="small" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </div>
                <div className={classes.statusRow}>
                    <ReservationStatusChip status={reservation.status} />
                    {reservation.status === 'Requested'
                        ? i18n.t('reservations.channelReceived', {
                              channel,
                              dateTime: formatReservationDate(
                                  reservation.createDate,
                                  'dd.MM.yyyy, HH:mm'
                              ),
                          })
                        : channel}
                </div>

                <div className={classes.caption}>{i18n.t('reservations.sections.reservation')}</div>
                <div className={classes.grid}>
                    {field(
                        i18n.t('reservations.fields.date'),
                        formatReservationDate(
                            reservation.reservationStartDateTime,
                            'EEE, dd.MM.yyyy'
                        )
                    )}
                    {field(
                        i18n.t('reservations.fields.time'),
                        `${getTimePart(reservation.reservationStartDateTime)} – ${getTimePart(
                            reservation.reservationEndDateTime
                        )} (${formatDuration(reservation.reservationDurationInMinutes)})`
                    )}
                    {field(i18n.t('reservations.fields.guests'), formatGuestsCount(reservation))}
                    {field(
                        i18n.t('reservations.columns.table'),
                        assignedTables ? (
                            `${assignedTables} · ${formatSeats(
                                getSelectedSeats(reservation.tables, tables)
                            )}`
                        ) : (
                            <span className={classes.muted}>
                                {i18n.t('reservations.notAssigned')}
                            </span>
                        )
                    )}
                </div>

                <div className={classes.caption}>
                    {i18n.t('reservations.sections.clientInformation')}
                </div>
                {(reservation.firstName || reservation.lastName) && (
                    <div className={classes.contact}>
                        <PersonIcon />
                        {[reservation.firstName, reservation.lastName].filter(Boolean).join(' ')}
                    </div>
                )}
                {reservation.phone && (
                    <div className={classes.contact}>
                        <PhoneIcon />
                        <Link href={`tel:${reservation.phone}`}>{reservation.phone}</Link>
                    </div>
                )}
                {reservation.email ? (
                    <div className={classes.contact}>
                        <EmailIcon />
                        {reservation.email}
                    </div>
                ) : (
                    reservation.status !== 'Requested' && (
                        <div className={classes.contact}>
                            <EmailIcon />
                            <span className={classes.muted}>
                                {i18n.t('reservations.notEnteredYet')}
                            </span>
                        </div>
                    )
                )}

                {allergies.length > 0 && (
                    <>
                        <div className={classes.caption}>
                            {i18n.t('reservations.fields.allergies')}
                        </div>
                        <div className={classes.chips}>
                            {allergies.map((allergy) => (
                                <Chip key={allergy} label={allergy} />
                            ))}
                        </div>
                    </>
                )}

                {reservation.clientNote && (
                    <>
                        <div className={classes.caption}>
                            {i18n.t('reservations.fields.specialRequests')}
                        </div>
                        <div className={classes.text}>{reservation.clientNote}</div>
                    </>
                )}
            </div>
            {footer && <div className={classes.footer}>{footer}</div>}
        </div>
    );
}
